import { useState, useCallback, useContext, useRef, useEffect } from 'react';
import { B, space, font, radius } from '../constants/brand';
import { api } from '../api/client';
import { getAccessToken } from '../config/auth';
import { useApi } from '../hooks/useApi';
import { UserContext } from '../context/UserContext';
import { Btn, Spinner, Badge, useToast } from '../components/ui';
import { fmt } from '../utils/format';
import { AssigneePicker } from '../components/shared/AssigneePicker';
import { InlineSearchPicker, useDealPickerItems, dealPickerValueFor } from '../components/tasks/TaskComponents';
import { prepareFileForOcr, ocrFileNoun, runHandwritingOcr } from '../utils/handwritingOcr';
import { markdownToNoteHtml } from '../utils/noteMarkdown';
import { NoteBodyEditor, insertMarkdownAtEnd } from '../components/shared/NoteBodyEditor';
import { assignFilesToRows } from '@harvus/shared';

// =============================================================
// GlobalNotes — take one free-flowing brain-dump spanning MANY deals, let AI
// split it into sections, route each section to the right deal / deal-detail
// (as a note or a task), review + correct, then create everything through the
// existing /api/notes and /api/tasks endpoints.
//
// The global page is a CAPTURE FUNNEL — everything it creates lands as an
// ordinary note or task ON the destination deal (same storage as writing in
// context), so the deal's Notes & Tasks summary, search, and deep-links all
// still work. It does not replace per-deal notes; it feeds them.
//
// Reuses the parse engine (POST /api/dictation/parse → dictationParser.js),
// which now returns a `sourceExcerpt` per item so each routed section shows
// which part of what you wrote it came from — and, when the text names a
// specific workstream ("the zoning appeal on Riverbend"), a `projectId`
// already resolved against that deal's live dbo.DealProjects rows (see
// dictationParser.js's projectIndex rule). Projects are rows in each row's one
// "Goes to" list (deal > detail > project), so the AI's match shows as that
// project being chosen and is corrected in the same box.
//
// A row's projectId is cleared whenever it is re-routed to a deal or detail
// row (routeRow, below) — a project belongs to exactly one deal, so a project
// picked for one deal cannot silently ride along onto another.
//
// THE BRAIN-DUMP AND EVERY NOTE ROW ARE THE SAME BLOCKNOTE EDITOR THE DEAL
// NOTES USE (`NoteBodyEditor`), not a <textarea>. This page used to compose in
// plain text and file each section through `<p>` + `<br/>`, so bullets,
// numbering and headings — the structure every other note in the app carries —
// landed on the deal as literal "- item" / "## Title" text. Now:
//   · the compose box is the editor; what goes to the AI split is its MARKDOWN
//     (`blocksToMarkdownLossy`), and the parser is told to keep that structure
//     verbatim in each section's `content`;
//   · each NOTE row is the editor again, seeded from that Markdown, and files
//     its BlockNote JSON — exactly what "Write a note" on the deal stores;
//   · a TASK row keeps a plain textarea, because a task's description is plain
//     text everywhere else; toggling note ↔ task converts through the Markdown.
// `markdownToNoteHtml` (utils/noteMarkdown.js) is the fallback for a row whose
// editor never reported, so structure survives even then.
// =============================================================

const genReqId = () => (window.crypto?.randomUUID?.() || `c-${Date.now()}-${Math.random().toString(36).slice(2)}`);

// A routed note files as the editor's BlockNote JSON; a row that never
// reported falls back to the Markdown, converted with its structure intact.
export const noteContentFor = (row) => (row?.contentJson ? row.contentJson : markdownToNoteHtml(row?.content || ''));

const PRIORITY_OPTIONS = [
  { value: '', label: 'No priority' },
  { value: 'Critical', label: 'Critical' },
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
];

// Post a note through the existing endpoint via a raw fetch (longer timeout;
// the shared client retries POSTs and could otherwise duplicate a note).
async function postNote(body) {
  const token = await getAccessToken();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 60000);
  try {
    const res = await fetch('/api/notes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`Note create failed (${res.status})`);
    return res.json().catch(() => ({}));
  } finally {
    clearTimeout(timer);
  }
}

// `seedText` starts the page from an existing Notes-app note ("Split & route
// this note"): the text is split at once, nothing is typed, and the searchable
// table below is hidden (`embedded`). `onRouted({ notes, tasks })` is told when
// everything filed cleanly, so the Notes app can offer to retire the original —
// otherwise the same text lives in two places.
//
// `seedFiles` are the original note's files ({ fileId, name, text }): each
// routed NOTE row offers them as attachments, ticked on the sections that came
// from a file's text (and on every note when none did, so a file is never
// dropped). On Create each ticked file is copied server-side into the deal-note
// attachment container (POST /api/notebook-files/{id}/stage-attachment) once,
// and named in the note's `attachments`.
export function GlobalNotes({ onNoteOpen, seedText = '', seedFiles = [], embedded = false, onRouted }) {
  const user = useContext(UserContext);
  const { toast } = useToast();

  const [phase, setPhase]   = useState('compose'); // compose | parsing | review
  // The compose box is a BlockNote editor; `text` is its live MARKDOWN — what
  // the AI split reads — and `composeEditorRef` is the editor itself, so a
  // handwriting scan can be inserted without re-seeding what was typed.
  const [text, setText]     = useState(seedText || '');
  const composeEditorRef    = useRef(null);
  const [rows, setRows]     = useState([]);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const cameraInputRef = useRef();
  const uploadInputRef = useRef();

  // Searchable table of ALL notes across deals, deal-details, and tasks.
  const [search, setSearch]         = useState('');
  const [noteList, setNoteList]     = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listHasMore, setListHasMore] = useState(false);
  const [listReloadKey, setListReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setListLoading(true);
      try {
        const qs = `notes/list?limit=100${search.trim() ? `&q=${encodeURIComponent(search.trim())}` : ''}`;
        const res = await api.get(qs);
        if (!cancelled) { setNoteList(res?.notes || []); setListHasMore(!!res?.hasMore); }
      } catch {
        if (!cancelled) { setNoteList([]); setListHasMore(false); }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    }, search.trim() ? 300 : 0); // debounce typing; load immediately on mount/reload
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, listReloadKey]);

  const openNote = (n) => {
    if (!onNoteOpen) return;
    onNoteOpen({
      type: 'note',
      noteId: n.noteID,
      taskId: n.taskID || null,
      dealId: n.dealID || null,
      dealDetailId: n.dealDetailID || null,
    });
  };

  const { data: users, loading: usersLoading } = useApi(() => api.get('users'), []);
  const { data: contacts } = useApi(() => api.get('contacts'), []);
  const { dealPickerItems } = useDealPickerItems({ includeProjects: true });

  const canTask = user?.role === 'Admin';

  // "Start over" returns to the compose box with what was typed still in it
  // (the editor re-seeds from `text`), so "go back and add more detail" is a
  // real option rather than a retype.
  const reset = useCallback(() => { setPhase('compose'); setRows([]); setSaving(false); }, []);

  // Scan/upload a handwritten (or printed) page — a photo or a scanned PDF —
  // → OCR → append the recognized text to the brain-dump, so it gets split +
  // routed like anything you type. Reuses the same endpoint the note composer
  // uses (Azure Document Intelligence + Claude vision).
  const [scanStatus, setScanStatus] = useState('');
  const runOcr = useCallback(async (file) => {
    if (!file) return;
    const noun = ocrFileNoun(file);
    setScanning(true);
    try {
      const { base64, contentType } = await prepareFileForOcr(file);
      // Fire-and-poll: a long PDF outlives the edge's ~45 s, so the request's
      // response is not the outcome — the job row is (utils/handwritingOcr.js).
      const data = await runHandwritingOcr({ base64, contentType, getAccessToken, onStatus: setScanStatus });
      const recognized = (data.markdown || data.text || '').trim();
      if (recognized) {
        // Into the editor (its onChange then updates `text`); with no editor
        // mounted, onto the Markdown directly so nothing recognized is lost.
        const inserted = composeEditorRef.current ? await insertMarkdownAtEnd(composeEditorRef.current, recognized) : false;
        if (!inserted) setText(prev => (prev.trim() ? `${prev}\n\n${recognized}` : recognized));
        toast.success(`Added text from your ${noun}.`);
      } else {
        toast.info(`Couldn't read any text from that ${noun}.`);
      }
    } catch (err) {
      toast.error(err.message || `Could not scan the ${noun}.`);
    } finally {
      setScanning(false);
      setScanStatus('');
    }
  }, [toast]);

  const onPickImage = (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) runOcr(f); };

  // Resolve a routed row to the InlineSearchPicker value shape.
  // A row tagged to a project shows its project row (the most specific choice),
  // provided that project is offered in the list.
  const rowValue = (r) => dealPickerValueFor({
    dealId: r.dealId, dealDetailId: r.dealDetailId,
    projectId: r.projectId && dealPickerItems.some(i => i.value === `project:${r.projectId}`) ? r.projectId : null,
  });

  const parse = useCallback(async () => {
    const t = text.trim();
    if (!t) return;
    setPhase('parsing');
    try {
      const token = await getAccessToken();
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 60000);
      let data;
      try {
        const res = await fetch('/api/dictation/parse', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ text: t }),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Split failed (${res.status})`);
        data = await res.json();
      } finally { clearTimeout(timer); }

      const items = Array.isArray(data.items) ? data.items : [];
      const mapped = items.map((it) => ({
        include: true,
        reqId: genReqId(),
        type: it.type === 'task' ? 'task' : 'note',
        title: it.title || '',
        content: it.content || '',   // Markdown — the structure the writer gave it
        contentJson: null,           // BlockNote JSON once the row's editor reports
        sourceExcerpt: it.sourceExcerpt || '',
        confidence: typeof it.confidence === 'number' ? it.confidence : null,
        dealId: it.dealId || null,
        dealDetailId: it.dealDetailId || null,
        // What the AI proposed, kept unchanged so the save can report what the
        // person re-routed, re-typed or dropped (the feedback loop).
        ai: { type: it.type === 'task' ? 'task' : 'note', dealId: it.dealId || null, dealDetailId: it.dealDetailId || null },
        projectId: it.projectId || null,  // AI-matched workstream, or null — see header comment
        dueDate: it.dueDate || '',
        priority: it.priority || '',
        assignees: (it.assigneeIds || [])
          .map(id => { const u = (users || []).find(x => x.userID === id); return u ? { type: 'user', id, name: u.displayName } : null; })
          .filter(Boolean),
        error: null,
      }));
      // The original note's files, attached to the sections they fed.
      const perRow = assignFilesToRows(seedFiles, mapped);
      setRows(mapped.map((r, i) => ({ ...r, files: perRow[i] })));
      setPhase('review');
    } catch (err) {
      toast.error(err.name === 'AbortError' ? 'This took too long — please try again.' : (err.message || 'Could not split the note.'));
      setPhase('compose');
    }
  }, [text, users, toast, seedFiles]);

  // A seeded note is split as soon as the people list has loaded (a task's
  // assignees are resolved against it — a failed load still splits), once.
  const autoSplit = useRef(false);
  useEffect(() => {
    if (!seedText?.trim() || autoSplit.current || usersLoading) return;
    autoSplit.current = true;
    parse();
  }, [seedText, usersLoading, parse]);

  const patchRow = (idx, patch) => setRows(rs => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  // Resolve a selection in the one Deal / Detail / Project list back to ids.
  // A project row sets its deal, its detail and the project together; any
  // other row CLEARS projectId (see header comment) — a project matched for the
  // old routing has no standing on the new one.
  const routeRow = (idx, value) => {
    if (!value) { patchRow(idx, { dealId: null, dealDetailId: null, projectId: null, error: null }); return; }
    const item = dealPickerItems.find(i => i.value === value);
    if (!item) return;
    patchRow(idx, { dealId: item.dealID || null, dealDetailId: item.dealDetailID || null, projectId: item.projectID || null, error: null });
  };

  const included = rows.filter(r => r.include);
  const noteCount = included.filter(r => r.type === 'note').length;
  const taskCount = included.filter(r => r.type === 'task').length;

  const confirm = useCallback(async () => {
    let bad = false;
    setRows(rs => rs.map(r => {
      if (!r.include) return { ...r, error: null };
      if (!r.dealId) { bad = true; return { ...r, error: 'Pick a deal' }; }
      if (r.type === 'note' && !r.content.trim()) { bad = true; return { ...r, error: 'Empty note' }; }
      if (r.type === 'task' && !r.title.trim() && !r.content.trim()) { bad = true; return { ...r, error: 'Empty task' }; }
      return { ...r, error: null };
    }));
    if (bad) { toast.error('Fix the highlighted items first.'); return; }

    setSaving(true);
    let notesOk = 0, tasksOk = 0, failed = 0, attachedNotes = 0, fileFailures = 0;
    const savedIdx = new Set();
    // Each original file is copied ONCE and the copy named by every routed
    // note that keeps it (the attachment rows point at the same staged blob).
    const staged = new Map();
    const stageFile = async (f) => {
      if (!staged.has(f.fileId)) {
        staged.set(f.fileId, api.post(`notebook-files/${f.fileId}/stage-attachment`, {}, { timeout: 120000 })
          .then(res => (res?.available ? {
            fileName: res.fileName, contentType: res.contentType, fileSizeBytes: res.fileSizeBytes,
            blobContainer: res.blobContainer, blobPath: res.blobPath,
          } : null))
          .catch(() => null));
      }
      return staged.get(f.fileId);
    };
    for (const [idx, r] of rows.entries()) {
      if (!r.include) continue;
      try {
        if (r.type === 'task') {
          await api.post('tasks', {
            title: (r.title || r.content).trim(),
            description: r.content && r.content.trim() !== (r.title || '').trim() ? r.content.trim() : null,
            dealId: r.dealId,
            dealDetailId: r.dealDetailId || null,
            projectId: r.projectId || null,
            dueDate: r.dueDate || null,
            priority: r.priority || 'Medium',
            status: 'Not Started',
            assigneeIds: r.assignees.filter(a => a.type === 'user').map(a => a.id),
            contactAssigneeIds: r.assignees.filter(a => a.type === 'contact').map(a => a.id),
            clientRequestId: r.reqId,
            // Rows were reviewed one-by-one here, so bypass the interactive
            // near-duplicate gate (mirrors the dictation widget / bot bulk).
            confirmDuplicate: true,
          });
          tasksOk++;
        } else {
          const wanted = (r.files || []).filter(f => f.include);
          const attachments = (await Promise.all(wanted.map(stageFile))).filter(Boolean);
          fileFailures += wanted.length - attachments.length;
          await postNote({ dealId: r.dealId, dealDetailId: r.dealDetailId || null, projectId: r.projectId || null, content: noteContentFor(r), attachments });
          notesOk++;
          if (attachments.length) attachedNotes++;
        }
        savedIdx.add(idx);
      } catch { failed++; }
    }
    if (savedIdx.size) setRows(rs => rs.map((r, i) => (savedIdx.has(i) ? { ...r, include: false } : r)));
    setSaving(false);
    reportSplitFeedback(rows, savedIdx, dealPickerItems);

    const parts = [];
    if (notesOk) parts.push(`${notesOk} note${notesOk === 1 ? '' : 's'}`);
    if (tasksOk) parts.push(`${tasksOk} task${tasksOk === 1 ? '' : 's'}`);
    if (parts.length) toast.success(`Filed ${parts.join(' and ')} onto their deals.`);
    if (failed) toast.error(`${failed} item${failed === 1 ? '' : 's'} could not be created.`);
    if (fileFailures) toast.error(`${fileFailures} file${fileFailures === 1 ? '' : 's'} could not be attached — the notes were filed without them.`);
    if (notesOk || tasksOk) setListReloadKey(k => k + 1); // refresh the notes table
    if (!failed) {
      setText(''); reset();
      if (onRouted && (notesOk || tasksOk)) onRouted({ notes: notesOk, tasks: tasksOk, attached: attachedNotes, fileFailures });
    }
  }, [rows, toast, reset, dealPickerItems, onRouted]);

  // ── Review row ──────────────────────────────────────────────────────────
  const renderRow = (r, idx) => (
    <div key={idx} style={{
      border: `1px solid ${r.error ? B.red : B.border}`, borderRadius: radius.lg,
      padding: space.md, marginBottom: space.md, background: r.include ? B.white : B.surfaceMuted,
      opacity: r.include ? 1 : 0.6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, marginBottom: space.sm }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: font.sm, color: B.textMd, cursor: 'pointer' }}>
          <input type="checkbox" checked={r.include} onChange={e => patchRow(idx, { include: e.target.checked })} /> Include
        </label>
        <div style={{ display: 'flex', gap: 4 }}>
          {(canTask ? ['note', 'task'] : ['note']).map(t => (
            <button key={t} type="button" onClick={() => patchRow(idx, { type: t, contentJson: null })}
              style={{
                border: `1px solid ${r.type === t ? B.navy : B.border}`, background: r.type === t ? B.navy : B.white,
                color: r.type === t ? B.white : B.textMd, borderRadius: radius.pill, padding: '2px 12px',
                fontSize: font.xs, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'capitalize',
              }}>{t}</button>
          ))}
        </div>
        {typeof r.confidence === 'number' && (
          <span title="AI confidence in the deal match" style={{ fontSize: font.xs, color: r.confidence >= 0.7 ? B.green : B.amber, fontWeight: 600 }}>
            {Math.round(r.confidence * 100)}% match
          </span>
        )}
        {r.error && <span style={{ marginLeft: 'auto', color: B.red, fontSize: font.xs, fontWeight: 600 }}>{r.error}</span>}
      </div>

      {r.sourceExcerpt && (
        <div style={{ fontSize: font.xs, color: B.textLt, fontStyle: 'italic', borderLeft: `2px solid ${B.border}`, paddingLeft: 8, marginBottom: space.sm }}>
          From what you wrote: “{r.sourceExcerpt}”
        </div>
      )}

      {r.type === 'task' && (
        <input value={r.title} onChange={e => patchRow(idx, { title: e.target.value })} placeholder="Task title"
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.base, color: B.text, fontFamily: 'inherit', outline: 'none', marginBottom: space.sm }} />
      )}

      {r.type === 'note' ? (
        // The same editor the deal's "Write a note" uses — bullets, numbering,
        // headings, checklists, / for the block menu. Keyed on the row so a
        // note ↔ task toggle re-seeds it from the row's Markdown.
        <NoteBodyEditor
          key={`${r.reqId}:note`}
          initialMarkdown={r.content}
          minHeight={72}
          onChange={({ content, markdown }) => patchRow(idx, { contentJson: content, content: markdown })}
          style={{ marginBottom: space.sm }}
        />
      ) : (
        <textarea value={r.content} onChange={e => patchRow(idx, { content: e.target.value, contentJson: null })}
          placeholder="Details (optional)" rows={2}
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.base, color: B.text, fontFamily: 'inherit', outline: 'none', resize: 'vertical', marginBottom: space.sm }} />
      )}

      {r.type === 'note' && r.files?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: space.sm }}>
          <span style={{ fontSize: font.xs, color: B.textMd, fontWeight: 600 }}>Attach from the original:</span>
          {r.files.map((f, fi) => (
            <label key={f.fileId} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: font.xs, color: B.text, cursor: 'pointer', border: `1px solid ${f.include ? B.navy : B.border}`, borderRadius: radius.pill, padding: '2px 10px' }}>
              <input type="checkbox" checked={f.include}
                onChange={e => patchRow(idx, { files: r.files.map((x, xi) => (xi === fi ? { ...x, include: e.target.checked } : x)) })} />
              {f.name}
            </label>
          ))}
        </div>
      )}

      <InlineSearchPicker
        label="Goes to"
        items={dealPickerItems}
        value={rowValue(r)}
        onChange={(v) => routeRow(idx, v)}
        placeholder="Search deals / deal details / projects…"
        clearLabel="— Not assigned —"
      />

      {r.type === 'task' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.sm }}>
          <input type="date" value={r.dueDate ? String(r.dueDate).slice(0, 10) : ''} onChange={e => patchRow(idx, { dueDate: e.target.value })}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.base, color: B.text, fontFamily: 'inherit', outline: 'none' }} />
          <select value={r.priority || ''} onChange={e => patchRow(idx, { priority: e.target.value })}
            style={{ width: '100%', padding: '8px 10px', border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.base, color: B.text, background: B.white, fontFamily: 'inherit', cursor: 'pointer' }}>
            {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div style={{ gridColumn: '1 / -1' }}>
            <AssigneePicker selected={r.assignees} onChange={sel => patchRow(idx, { assignees: sel })} users={users || []} contacts={contacts || []} />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: space.xxl }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: space.md, marginBottom: space.lg, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: font.xl, fontWeight: 700, color: B.text, margin: 0 }}>{embedded ? 'Split & route this note' : 'Notes'}</h1>
          <div style={{ fontSize: font.sm, color: B.textLt, marginTop: 2 }}>
            {embedded
              ? 'AI splits the note into sections and puts each one on its deal as a note or a task. Check each one before you create them.'
              : 'Brain-dump about any deals in one place — AI sorts each part onto the right deal as a note or a task. Every note across deals and tasks is searchable below.'}
          </div>
        </div>
        {phase === 'review' && <Badge color={B.navy}>{`${noteCount} note${noteCount === 1 ? '' : 's'} · ${taskCount} task${taskCount === 1 ? '' : 's'}`}</Badge>}
      </div>

      {phase === 'compose' && (
        <>
          <label style={{ display: 'block', fontSize: font.sm, fontWeight: 600, color: B.textMd, marginBottom: 6 }}>What do you want to capture?</label>
          <NoteBodyEditor
            editorRef={composeEditorRef}
            initialMarkdown={text}
            minHeight={260}
            autoFocus
            onChange={({ markdown }) => setText(markdown)}
            style={{ marginBottom: space.sm }}
          />
          <div style={{ fontSize: font.xs, color: B.textLt, marginBottom: space.md }}>
            Type or dictate freely — a deal&apos;s name on its own line routes everything under it. Bullets, numbering, headings and checklists carry through to the note (type / for the block menu).
            {!canTask && ' Your role can create notes only.'}
          </div>

          {/* Handwriting capture — scan a page with the camera, or upload a photo
              OR a scanned PDF of one; the text is read out and dropped into the
              note above, then split like any text. The camera input stays
              image-only (a camera cannot produce a PDF); the upload input takes both. */}
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={onPickImage} style={{ display: 'none' }} />
          <input ref={uploadInputRef} type="file" accept="image/*,application/pdf,.pdf" onChange={onPickImage} style={{ display: 'none' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap', marginBottom: space.md }}>
            <Btn variant="ghost" size="sm" onClick={() => cameraInputRef.current?.click()} disabled={scanning}>📷 Scan handwriting</Btn>
            <Btn variant="ghost" size="sm" onClick={() => uploadInputRef.current?.click()} disabled={scanning}>🖼 Upload image or PDF</Btn>
            {scanning && <span style={{ fontSize: font.xs, color: B.textLt }}>{scanStatus || 'Reading text…'}</span>}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Btn onClick={parse} disabled={!text.trim() || scanning}>Split with AI</Btn>
          </div>
        </>
      )}

      {phase === 'parsing' && <div style={{ padding: space.xxl }}><Spinner label="Sorting your note into deals…" /></div>}

      {phase === 'review' && (
        <>
          {rows.length === 0
            ? <div style={{ color: B.textMd, fontSize: font.base }}>Nothing to create. Go back and add more detail.</div>
            : rows.map((r, i) => renderRow(r, i))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.md, marginTop: space.lg }}>
            <Btn variant="ghost" size="sm" onClick={reset} disabled={saving}>Start over</Btn>
            <Btn onClick={confirm} loading={saving} disabled={included.length === 0}>
              {included.length === 0 ? 'Nothing selected' : `Create ${[noteCount && `${noteCount} note${noteCount === 1 ? '' : 's'}`, taskCount && `${taskCount} task${taskCount === 1 ? '' : 's'}`].filter(Boolean).join(' & ')}`}
            </Btn>
          </div>
        </>
      )}

      {/* ── All notes — searchable table across deals, deal details, and tasks ── */}
      {!embedded && (
      <div style={{ marginTop: space.xxl, borderTop: `1px solid ${B.border}`, paddingTop: space.lg }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: space.md, marginBottom: space.md, flexWrap: 'wrap' }}>
          <div style={{ fontSize: font.lg, fontWeight: 700, color: B.text }}>All notes</div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search notes, deals, tasks…"
            style={{ flex: '1 1 260px', maxWidth: 360, boxSizing: 'border-box', padding: '9px 12px', border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.base, color: B.text, fontFamily: 'inherit', outline: 'none' }}
          />
        </div>

        <div style={{ border: `1px solid ${B.border}`, borderRadius: radius.lg, overflow: 'hidden', background: B.white }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: font.sm }}>
              <thead>
                <tr style={{ background: B.surfaceMuted }}>
                  {['Date', 'Note', 'Attached to', 'Author'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '9px 12px', fontSize: font.xs, fontWeight: 700, color: B.textMd, textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {noteList.map(n => {
                  const chip = n.scope === 'task'
                    ? { label: 'Task', color: B.amber }
                    : n.scope === 'detail'
                      ? { label: 'Detail', color: B.tealDk }
                      : { label: 'Deal', color: B.navy };
                  const where = n.scope === 'task'
                    ? (n.taskTitle || 'Task')
                    : n.scope === 'detail'
                      ? [n.dealName, n.detailName].filter(Boolean).join(' · ')
                      : (n.dealName || '—');
                  return (
                    <tr key={n.noteID}
                      onClick={() => openNote(n)}
                      style={{ borderTop: `1px solid ${B.border}`, cursor: onNoteOpen ? 'pointer' : 'default' }}
                      onMouseEnter={e => { e.currentTarget.style.background = B.surfaceMuted; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <td style={{ padding: '10px 12px', color: B.textMd, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                        {fmt.date(n.noteDate || n.createdUTC)}
                      </td>
                      <td style={{ padding: '10px 12px', color: B.text, minWidth: 240, maxWidth: 460 }}>
                        <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {n.snippet || '(no text)'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: B.textMd, verticalAlign: 'top', minWidth: 160 }}>
                        <span style={{ display: 'inline-block', fontSize: font.xs, fontWeight: 700, color: '#fff', background: chip.color, borderRadius: radius.pill, padding: '1px 8px', marginRight: 6 }}>{chip.label}</span>
                        {where}
                      </td>
                      <td style={{ padding: '10px 12px', color: B.textMd, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{n.author || '—'}</td>
                    </tr>
                  );
                })}
                {noteList.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: '18px 12px', color: B.textLt, textAlign: 'center' }}>
                    {listLoading ? 'Loading…' : (search.trim() ? 'No notes match your search.' : 'No notes yet.')}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {listHasMore && (
          <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 6 }}>
            Showing the first 100 — refine your search to narrow the list.
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// Feedback for the note splitter (POST /api/ai-corrections): for every piece
// that was saved or deliberately left out, what the AI proposed and what the
// person made of it. Best-effort and silent — a lost report never costs the
// notes that were just filed.
function reportSplitFeedback(rows, savedIdx, pickerItems) {
  const labelFor = (dealId, dealDetailId) => {
    const hit = (pickerItems || []).find(i => (dealDetailId ? i.dealDetailID === dealDetailId : (i.dealID === dealId && !i.dealDetailID)));
    return hit ? (hit.label || hit.name || null) : null;
  };
  const items = [];
  rows.forEach((r, idx) => {
    if (!r.ai) return;
    const saved = savedIdx.has(idx);
    if (!saved && r.include) return;             // failed to save: not a decision
    const before = { type: r.ai.type, dealId: r.ai.dealId, dealDetailId: r.ai.dealDetailId, dealName: labelFor(r.ai.dealId, r.ai.dealDetailId) };
    const after = saved
      ? { type: r.type, dealId: r.dealId, dealDetailId: r.dealDetailId || null, dealName: labelFor(r.dealId, r.dealDetailId) }
      : null;
    const rerouted = saved && ((before.dealId || null) !== (after.dealId || null) || (before.dealDetailId || null) !== (after.dealDetailId || null));
    items.push({
      action: !saved ? 'dismissed' : rerouted ? 'rerouted' : 'accepted',
      dealId: after ? after.dealId : before.dealId,
      before, after,
      input: { sourceExcerpt: (r.sourceExcerpt || '').slice(0, 2000) },
    });
  });
  if (items.length) api.post('ai-corrections', { job: 'globalNoteSplit', items }).catch(() => {});
}

export default GlobalNotes;
